import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiSearch, FiLoader, FiUserPlus, FiCheck, FiClock, FiUserX, FiMail } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { searchUsers, sendFriendRequest } from '../services/friendService';
import { checkFriendCapacity, isPlanCapError, parsePlanCapError } from '../services/planService';
import PaywallModal from './PaywallModal';

function FriendSearchModal({ isOpen, onClose, onRequestSent }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [sentIds, setSentIds] = useState(new Set());
  const [error, setError] = useState('');
  const [paywallFeature, setPaywallFeature] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setTimeout(() => inputRef.current?.focus(), 100);
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  const handleSearch = useCallback(async () => {
    if (!searchTerm || !searchTerm.includes('@')) {
      return;
    }

    setSearching(true);
    setSearched(false);
    setError('');
    const { data, error: searchError } = await searchUsers(searchTerm);
    if (searchError) {
      setError('Failed to search users');
      setSearched(false);
    } else {
      setResults(data);
      setSearched(true);
    }
    setSearching(false);
  }, [searchTerm]);

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setSearched(false);
    setResults([]);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleSendRequest = async (userId) => {
    setSendingId(userId);
    setError('');
    try {
      // Preflight: if I'm already at-cap, the mutual auto-accept path would
      // fail at the trigger. Surface the paywall before the network call.
      const cap = await checkFriendCapacity();
      if (!cap.allowed) {
        setPaywallFeature('friends');
        return;
      }

      const { error: sendError, autoAccepted } = await sendFriendRequest(userId);
      if (sendError) {
        if (isPlanCapError(sendError)) {
          const parsed = parsePlanCapError(sendError);
          setPaywallFeature(parsed?.side === 'other' ? 'friends_other' : 'friends');
        } else {
          setError(sendError.message || 'Failed to send request');
        }
      } else {
        setSentIds(prev => new Set([...prev, userId]));
        if (autoAccepted) {
          setResults(prev => prev.map(r =>
            r.id === userId ? { ...r, friendship_status: 'accepted' } : r
          ));
        }
        if (onRequestSent) onRequestSent();
        setTimeout(() => handleClose(), 1000);
      }
    } finally {
      setSendingId(null);
    }
  };

  const handleClose = () => {
    onClose();
    setSearchTerm('');
    setResults([]);
    setError('');
    setSearched(false);
    setSentIds(new Set());
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getStatusButton = (user) => {
    const status = sentIds.has(user.id) ? 'pending' : user.friendship_status;

    switch (status) {
      case 'accepted':
        return (
          <span className="inline-flex items-center text-xs font-medium text-accent px-2 py-1 bg-accent/10 rounded-lg">
            <SafeIcon icon={FiCheck} className="w-3 h-3 mr-1" />
            Friends
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center text-xs font-medium text-text-muted px-2 py-1 bg-surface rounded-lg">
            <SafeIcon icon={FiClock} className="w-3 h-3 mr-1" />
            Pending
          </span>
        );
      case 'blocked':
        return (
          <span className="inline-flex items-center text-xs font-medium text-text-muted px-2 py-1 bg-surface rounded-lg">
            <SafeIcon icon={FiUserX} className="w-3 h-3 mr-1" />
            Blocked
          </span>
        );
      default:
        return (
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => handleSendRequest(user.id)}
            disabled={sendingId === user.id}
            className="inline-flex items-center text-xs font-medium text-white bg-accent px-3 py-1.5 rounded-lg hover:bg-interactive-hover transition-colors disabled:opacity-50"
          >
            {sendingId === user.id ? (
              <SafeIcon icon={FiLoader} className="w-3 h-3 animate-spin" />
            ) : (
              <>
                <SafeIcon icon={FiUserPlus} className="w-3 h-3 mr-1" />
                Add
              </>
            )}
          </motion.button>
        );
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: isMobile ? 50 : 0 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: isMobile ? 50 : 0 }}
          className={`bg-card border border-border-subtle w-full ${
            isMobile
              ? 'rounded-t-2xl max-h-[95vh] fixed bottom-0 left-0 right-0 overflow-y-auto'
              : 'rounded-lg max-w-md mx-auto max-h-[80vh] overflow-y-auto'
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={isMobile ? 'p-6 pb-24' : 'p-8'}>
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex-1 min-w-0">
                <h2 className="text-h3 text-text-primary mb-1 truncate">Find Friends</h2>
                <p className="text-body text-text-secondary">Enter their email address to add them</p>
              </div>
              <button
                onClick={handleClose}
                className="w-11 h-11 bg-surface rounded-xl flex items-center justify-center hover:bg-card transition-colors duration-200 ml-4 flex-shrink-0"
                aria-label="Close search modal"
              >
                <SafeIcon icon={FiX} className="w-5 h-5 text-text-secondary" />
              </button>
            </div>

            {/* Search Input */}
            <div className="flex gap-2 items-stretch mb-4">
              <input
                ref={inputRef}
                type="email"
                value={searchTerm}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                className="flex-1 min-w-0 px-4 py-3 border border-border-subtle rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent bg-card text-text-primary placeholder-gray-400 min-h-[44px] text-base"
                placeholder="Enter exact email address..."
                aria-label="Email address to search"
              />
              <button
                onClick={handleSearch}
                disabled={searching || !searchTerm.includes('@')}
                aria-label="Search"
                className="flex-shrink-0 w-11 h-11 inline-flex items-center justify-center bg-accent text-white rounded-lg hover:bg-interactive-hover transition-colors disabled:opacity-50"
              >
                {searching ? (
                  <SafeIcon icon={FiLoader} className="w-5 h-5 animate-spin" />
                ) : (
                  <SafeIcon icon={FiSearch} className="w-5 h-5" />
                )}
              </button>
            </div>

            {/* Error */}
            {error && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-scarlet-50 border border-scarlet-200 text-scarlet-700 px-4 py-3 rounded-lg text-sm font-medium mb-4"
              >
                {error}
              </motion.div>
            )}

            {/* Results */}
            <div className="space-y-2">
              {results.map((user) => (
                <div
                  key={user.id}
                  className="flex items-center justify-between p-3 rounded-lg hover:bg-surface transition-colors"
                >
                  <div className="flex items-center space-x-3 min-w-0">
                    <div className="w-10 h-10 bg-gradient-to-br from-accent/20 to-accent/30 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-accent">{getInitials(user.full_name)}</span>
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-text-primary text-sm truncate">{user.full_name || 'Unknown'}</p>
                      <p className="text-xs text-text-muted truncate">{user.email}</p>
                    </div>
                  </div>
                  <div className="flex-shrink-0 ml-3">
                    {getStatusButton(user)}
                  </div>
                </div>
              ))}

              {searched && !searching && results.length === 0 && (
                <div className="text-center py-6 px-2">
                  <div className="w-12 h-12 bg-surface rounded-lg flex items-center justify-center mx-auto mb-4 border border-border-subtle">
                    <SafeIcon icon={FiMail} className="w-5 h-5 text-text-muted" />
                  </div>
                  <p className="text-sm font-medium text-text-primary mb-1">
                    Not on TeeSync yet
                  </p>
                  <p className="text-xs text-text-muted">
                    We couldn&rsquo;t find anyone with that email. Make sure they&rsquo;ve joined TeeSync first.
                  </p>
                </div>
              )}

              {searchTerm.length > 0 && !searchTerm.includes('@') && (
                <div className="text-center py-8">
                  <p className="text-sm text-text-muted">Enter a full email address to search</p>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
      <PaywallModal
        isOpen={!!paywallFeature}
        onClose={() => setPaywallFeature(null)}
        feature={paywallFeature}
      />
    </AnimatePresence>
  );
}

export default FriendSearchModal;
